import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";
import Fullscreen from "yet-another-react-lightbox/plugins/fullscreen";
import Slideshow from "yet-another-react-lightbox/plugins/slideshow";
import Zoom from "yet-another-react-lightbox/plugins/zoom";
import { fetchGitHubContent } from "../../utils/githubMediaUtils";
import { FaSpinner } from "react-icons/fa";

// Default client images as fallback
const defaultClients = [
  {
    image: "https://via.placeholder.com/200x100?text=Client+1",
    name: "Client 1"
  },
  {
    image: "https://via.placeholder.com/200x100?text=Client+2",
    name: "Client 2"
  },
  {
    image: "https://via.placeholder.com/200x100?text=Client+3",
    name: "Client 3"
  },
  {
    image: "https://via.placeholder.com/200x100?text=Client+4",
    name: "Client 4"
  },
  {
    image: "https://via.placeholder.com/200x100?text=Client+5",
    name: "Client 5"
  },
  {
    image: "https://via.placeholder.com/200x100?text=Client+6",
    name: "Client 6"
  }
];

const ClientsSection = () => {
  const { t, i18n } = useTranslation();
  const [isRTL, setIsRTL] = useState(false);
  const language = i18n.language;
  
  // State for client images
  const [clientImages, setClientImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  
  // Lightbox state
  const [isOpen, setIsOpen] = useState(false);
  const [photoIndex, setPhotoIndex] = useState(0);

  useEffect(() => {
    setIsRTL(language === "ar");
  }, [language]);
  
  // Fetch client images from GitHub
  useEffect(() => {
    const fetchClientImages = async () => {
      setLoading(true);
      try {
        // Try to fetch media from the 'clients' folder
        const clientsMedia = await fetchGitHubContent('clients');
        
        // Filter for image files only
        const images = clientsMedia
          .filter(item => !item.isFolder && item.mediaType === 'image')
          .map(item => ({
            image: item.url,
            name: item.name
          }));
        
        if (images && images.length > 0) {
          setClientImages(images);
          setError(false);
        } else {
          // No images found
          setError(true);
          setClientImages([]);
        }
      } catch (error) {
        console.error('Error fetching client images:', error);
        // Set error state
        setError(true);
        setClientImages([]);
      } finally {
        setLoading(false);
      }
    };
    
    fetchClientImages();
  }, []);

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.5 }
    }
  };

  // Group clients into rows for grid display
  const clientRows = [];
  const clientsPerRow = {
    xs: 2, // mobile
    sm: 3, // tablet
    md: 4, // desktop
    lg: 5  // large desktop
  };

  // Ensure clientImages is always an array
  const safeClientImages = Array.isArray(clientImages) ? clientImages : [];

  // Create rows for grid layout
  for (let i = 0; i < safeClientImages.length; i += clientsPerRow.md) {
    clientRows.push(safeClientImages.slice(i, i + clientsPerRow.md));
  }
  
  // Open lightbox with the selected image
  const openLightbox = (index) => {
    setPhotoIndex(index);
    setIsOpen(true);
  };
  
  // Prepare slides for lightbox
  const slides = safeClientImages.map(client => ({
    src: client.image
  }));

  return (
    <div id="Clients" className="bg-gray-900 py-20 md:py-28">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-16"
          dir={isRTL ? "rtl" : "ltr"}
        >
          <h2 className="text-primary text-xl uppercase font-semibold tracking-wider mb-2">
            {t("trusted_by")}
          </h2>
          <h1 className="text-4xl md:text-5xl lg:text-6xl text-white font-bold">
            {t("our_clients")}
          </h1>
          <div className="w-24 h-1 bg-primary mx-auto mt-6"></div>
          <p className="text-gray-400 max-w-2xl mx-auto mt-6">
            {t("clients_description")}
          </p>
        </motion.div>

        {loading ? (
          <div className="flex justify-center items-center py-20">
            <FaSpinner className="animate-spin text-primary text-4xl" />
          </div>
        ) : error ? (
          <div className="flex justify-center items-center py-20">
            <div className="text-center">
              <p className="text-2xl text-red-400 mb-4">{t("clients_not_found")}</p>
              <p className="text-gray-400">{t("please_try_again_later")}</p>
            </div>
          </div>
        ) : (
          <>
            {/* Grid display for larger screens */}
            <motion.div
              className="hidden md:block"
              variants={containerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
            >
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-6 md:gap-8">
                {safeClientImages.map((client, idx) => (
                  <motion.div
                    key={idx}
                    variants={itemVariants}
                    whileHover={{ 
                      scale: 1.05, 
                      boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)"
                    }}
                    className="bg-gray-800 rounded-lg p-6 flex items-center justify-center group relative overflow-hidden cursor-pointer"
                    onClick={() => openLightbox(idx)}
                  >
                    <div className="absolute inset-0 bg-primary opacity-0 group-hover:opacity-10 transition-opacity duration-300"></div>
                    <img
                      src={client.image}
                      alt={`Client ${idx + 1}`}
                      className="w-full h-auto object-contain filter grayscale hover:grayscale-0 transition-all duration-300"
                      onError={(e) => {
                        e.target.src = "/images/placeholder.jpg";
                      }}
                    />
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Carousel for mobile devices */}
            <div className="md:hidden overflow-x-auto pb-6">
              <div className="flex space-x-4" style={{ width: `${safeClientImages.length * 200}px` }}>
                {safeClientImages.map((client, idx) => (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, x: 20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3, delay: idx * 0.1 }}
                    viewport={{ once: true }}
                    className="bg-gray-800 rounded-lg p-6 flex-shrink-0 w-40 h-40 flex items-center justify-center cursor-pointer"
                    onClick={() => openLightbox(idx)}
                  >
                    <img
                      src={client.image}
                      alt={`Client ${idx + 1}`}
                      className="w-full h-auto object-contain filter grayscale hover:grayscale-0 transition-all duration-300"
                      onError={(e) => {
                        e.target.src = "/images/placeholder.jpg";
                      }}
                    />
                  </motion.div>
                ))}
              </div>
            </div>
          </>
        )}

        {/* Call to action */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          viewport={{ once: true }}
          className="mt-16 text-center"
          dir={isRTL ? "rtl" : "ltr"}
        >
          <p className="text-gray-300 mb-6 max-w-xl mx-auto">
            {t("join_clients")}
          </p>
          <motion.a
            href="#Contact"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="inline-block bg-primary text-black font-semibold py-3 px-8 rounded-lg hover:bg-primary/90 transition-all duration-300"
          >
            {t("start_project_button")}
          </motion.a>
        </motion.div>
        
        {/* Lightbox for viewing client images */}
        <Lightbox
          open={isOpen}
          close={() => setIsOpen(false)}
          index={photoIndex}
          slides={slides}
          plugins={[Fullscreen, Slideshow, Zoom]}
        />
      </div>
    </div>
  );
};

export default ClientsSection; 